<section class="widget">
  <h2>{{ title }}</h2>
  <p class="intro">
    Flagged alerts grouped into weekly windows or custom calendar dates.
  </p>
  <div class="filter-controls">
    <div class="weeks">
      <button
        mat-stroked-button
        type="button"
        [class.active]="selectedWeek === 0"
        (click)="selectWeek(0)"
      >
        Last 7 days
      </button>
      <button
        mat-stroked-button
        type="button"
        [class.active]="selectedWeek === 1"
        (click)="selectWeek(1)"
      >
        Last 2 weeks
      </button>
      <button
        mat-stroked-button
        type="button"
        [class.active]="selectedWeek === 2"
        (click)="selectWeek(2)"
      >
        Last 3 weeks
      </button>
      <button
        mat-stroked-button
        type="button"
        [class.active]="selectedWeek === 3"
        (click)="selectWeek(3)"
      >
        Last 4 weeks
      </button>
    </div>

    <div class="calendar-inputs">
      <mat-form-field appearance="outline" class="date-field">
        <mat-label>Start date</mat-label>
        <input
          matInput
          [matDatepicker]="startPicker"
          [(ngModel)]="startDate"
          (dateChange)="onCustomDateChange()"
          placeholder="Select start date"
        />
        <mat-datepicker-toggle
          matIconSuffix
          [for]="startPicker"
        ></mat-datepicker-toggle>
        <mat-datepicker #startPicker></mat-datepicker>
      </mat-form-field>

      <mat-form-field appearance="outline" class="date-field">
        <mat-label>End date</mat-label>
        <input
          matInput
          [matDatepicker]="endPicker"
          [(ngModel)]="endDate"
          (dateChange)="onCustomDateChange()"
          placeholder="Select end date"
        />
        <mat-datepicker-toggle
          matIconSuffix
          [for]="endPicker"
        ></mat-datepicker-toggle>
        <mat-datepicker #endPicker></mat-datepicker>
      </mat-form-field>

      <button
        mat-flat-button
        color="primary"
        class="apply-btn"
        (click)="applyCustomRange()"
        [disabled]="loading || !startDate || !endDate"
        matTooltip="Apply custom calendar date range"
      >
        Apply dates
      </button>
    </div>
  </div>

  <div class="date-range">
    <strong>Date start:</strong> {{ displayStartDate }}
    <strong>Date end:</strong>
    {{ displayEndDate }}
  </div>
  <mat-form-field appearance="outline" class="customer-search-field">
    <mat-label>Search by customer ID</mat-label>
    <input
      matInput
      type="text"
      [(ngModel)]="customerIdSearch"
      placeholder="e.g. cust-123"
    />
    @if (customerIdSearch) {
      <button
        matSuffix
        mat-icon-button
        type="button"
        aria-label="Clear customer ID search"
        (click)="clearCustomerIdSearch()"
      >
        <mat-icon>close</mat-icon>
      </button>
    }
  </mat-form-field>
  <div class="summary-container">
    <div class="summary">
      <span
        >PII <b>{{ count('pii') }}</b></span
      >
      <span
        >Risky advice <b>{{ count('riskyAdvice') }}</b></span
      >
      <span
        >Smoke/fire <b>{{ count('smoke') }}</b></span
      >
    </div>
    @if (!loading && !error && findings.length) {
      <div class="widget-actions">
        <button
          mat-stroked-button
          type="button"
          class="toggle-results-btn"
          (click)="toggleShowResults()"
          [attr.aria-expanded]="showResults"
        >
          <mat-icon>{{ showResults ? 'expand_less' : 'expand_more' }}</mat-icon>
          {{ showResults ? 'Hide results' : 'Show results' }}
          ({{ findings.length }})
        </button>
        <button
          mat-stroked-button
          type="button"
          class="hide-completed-btn"
          (click)="toggleHideCompleted()"
          [attr.aria-pressed]="hideCompleted"
        >
          <mat-icon>{{
            hideCompleted ? 'visibility' : 'visibility_off'
          }}</mat-icon>
          {{ hideCompleted ? 'Show completed' : 'Hide completed' }}
          ({{ completedCount }})
        </button>
      </div>
    }
  </div>

  @if (loading) {
    <app-loading-state></app-loading-state>
  } @else if (error) {
    <app-error-state [message]="error"></app-error-state>
  } @else if (!findings.length) {
    <app-empty-state
      message="No matching findings were found for this week."
    ></app-empty-state>
  } @else if (showResults && !visibleFindings.length) {
    <app-empty-state
      [message]="
        customerIdSearch
          ? 'No findings match customer ID \'' + customerIdSearch + '\'.'
          : 'All findings in this range are marked as review complete.'
      "
    ></app-empty-state>
  } @else if (showResults) {
    <div class="alerts">
      @for (
        item of visibleFindings;
        track item.sk || item.sessionId || $index
      ) {
        <article
          (click)="selectFinding(item)"
          (keydown.enter)="selectFinding(item)"
          (keydown.space)="$event.preventDefault(); selectFinding(item)"
          tabindex="0"
          role="button"
          [attr.aria-expanded]="selectedFinding === item"
          matTooltip="Click to view analysis details"
        >
          <div class="card-top">
            <div class="card-title">
              <strong>{{ item.sk }}</strong>
              <small>
                {{ item.customerId || 'Unknown customer' }} ·
                {{ item.productType || 'Unknown product' }}
              </small>
            </div>
            <app-review-status-control
              class="review-corner"
              [reviewStatus]="item.reviewStatus"
              [reviewedBy]="item.reviewedBy"
              [reviewedAt]="item.reviewedAt"
              [loading]="isUpdatingReview(item)"
              (toggled)="toggleReviewStatus(item)"
            ></app-review-status-control>
          </div>
          <div class="badges">
            @if (item.pii) {
              <app-compliance-badge
                label="PII detected"
                tone="red"
              ></app-compliance-badge>
            }
            @if (item.riskyAdvice) {
              <app-compliance-badge
                label="Risky advice"
                tone="red"
              ></app-compliance-badge>
            }
            @if (item.smokeFireMentionCount) {
              <app-compliance-badge
                [label]="'Smoke/fire: ' + item.smokeFireMentionCount"
                tone="amber"
              ></app-compliance-badge>
            }
          </div>
          @if (selectedFinding === item) {
            <div class="finding-details">
              <section class="conversation-history">
                <h3>Conversation</h3>
                @if (!messageHistory(item).length) {
                  <p class="no-history">
                    The message history was not included with this alert.
                  </p>
                } @else {
                  <ol>
                    @for (message of messageHistory(item); track $index) {
                      <li>
                        <div>
                          <strong>{{ messageRole(message) }}</strong>
                          @if (messageTimestamp(message)) {
                            <time>{{ messageTimestamp(message) }}</time>
                          }
                        </div>
                        <p>
                          {{
                            messageText(message) ||
                              'No message content available.'
                          }}
                        </p>
                      </li>
                    }
                  </ol>
                }
              </section>
              <h3>Alert details</h3>
              <dl>
                <dt>Transcript timestamp</dt>
                <dd>{{ item.sk }}</dd>
                <dt>Customer ID</dt>
                <dd>{{ item.customerId || '—' }}</dd>
                <dt>Session ID</dt>
                <dd>{{ item.sessionId || '—' }}</dd>
                <dt>Plan number</dt>
                <dd>{{ item.planNumber || '—' }}</dd>
                <dt>Product / brand</dt>
                <dd>{{ item.productType || '—' }} / {{ item.brand || '—' }}</dd>
                <dt>Guardrail message</dt>
                <dd>{{ item.guardrailsMessage ? 'Yes' : 'No' }}</dd>
                <dt>Engineer requests</dt>
                <dd>{{ item.engineerRequestCount }}</dd>
                <dt>Human requests</dt>
                <dd>{{ item.humanRequestCount }}</dd>
                <dt>Negative sentiment</dt>
                <dd>{{ item.negativeSentimentCount }}</dd>
                <dt>Analysis status</dt>
                <dd>{{ item.analysisStatus }}</dd>
                <dt>Analysed at</dt>
                <dd>{{ item.analysedAt || '—' }}</dd>
                <dt>Review status</dt>
                <dd>
                  {{
                    item.reviewStatus === 'review-complete'
                      ? 'Review complete'
                      : 'Needs reviewing'
                  }}
                </dd>
                @if (item.reviewStatus === 'review-complete') {
                  <dt>Reviewed by</dt>
                  <dd>{{ item.reviewedBy || '—' }}</dd>
                  <dt>Reviewed at</dt>
                  <dd>
                    {{
                      item.reviewedAt ? (item.reviewedAt | date: 'medium') : '—'
                    }}
                  </dd>
                }
              </dl>
            </div>
          }
        </article>
      }
    </div>
  }
</section>
